import { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { SERVICE_LABELS, STATUS_LABELS, toLocalInput, todayKey, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const NONE = "none";

function initial(appt, defaultClientId, defaultStart, tz, userId) {
  if (appt) {
    return {
      client_id: appt.client_id, service_type: appt.service_type, start_local: toLocalInput(appt.start, tz),
      duration_minutes: appt.duration_minutes, price: appt.price, assigned_user_id: appt.assigned_user_id || NONE,
      status: appt.status, notes: appt.notes || "", checklist_template_id: NONE, scope: "this",
      frequency: "none", interval_days: 7, occurrences: 8, end_date: "",
    };
  }
  return {
    client_id: defaultClientId || "", service_type: "standard", start_local: defaultStart || `${todayKey(tz)}T09:00`,
    duration_minutes: 120, price: 0, assigned_user_id: userId, status: "confirmed", notes: "",
    checklist_template_id: NONE, scope: "this", frequency: "none", interval_days: 7, occurrences: 8, end_date: "",
  };
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-11" data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
}

function ConflictBox({ conflict, tz }) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-2" data-testid="conflict-warning">
      <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" />{conflict.message}</div>
      {conflict.conflicts?.map((c) => <div key={c.id}>Overlaps with {c.client_name} · {fmtDateTime(c.start, tz)}</div>)}
      {conflict.availability_warnings?.map((w) => <div key={w}>{w}</div>)}
    </div>
  );
}

export default function AppointmentFormDialog({ open, onOpenChange, appointment, defaultClientId, defaultStart, onSaved }) {
  const { tz, user } = useAuth();
  const [form, setForm] = useState(() => initial(null, defaultClientId, defaultStart, tz, user.id));
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [conflict, setConflict] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const editing = !!appointment;

  useEffect(() => {
    if (!open) return;
    setForm(initial(appointment, defaultClientId, defaultStart, tz, user.id));
    setConflict(null); setError("");
    Promise.all([api.get("/clients"), api.get("/team"), api.get("/checklist-templates")])
      .then(([c, t, tp]) => { setClients(c.data); setTeam(t.data); setTemplates(tp.data); })
      .catch((e) => setError(errMsg(e)));
  }, [open, appointment, defaultClientId, defaultStart, tz, user.id]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setConflict(null); };

  const submit = async (force = false) => {
    if (!form.client_id) return setError("Please choose a client.");
    setBusy(true); setError("");
    const base = {
      service_type: form.service_type, start_local: form.start_local, duration_minutes: Number(form.duration_minutes),
      price: Number(form.price) || 0, assigned_user_id: form.assigned_user_id === NONE ? null : form.assigned_user_id,
      notes: form.notes, force,
    };
    try {
      if (editing) {
        await api.put(`/appointments/${appointment.id}`, { ...base, scope: form.scope });
        toast.success("Appointment updated");
      } else {
        const { data } = await api.post("/appointments", {
          ...base, client_id: form.client_id, status: form.status,
          checklist_template_id: form.checklist_template_id === NONE ? null : form.checklist_template_id,
          recurrence: { frequency: form.frequency, interval_days: Number(form.interval_days), occurrences: Number(form.occurrences), end_date: form.end_date || null },
        });
        toast.success(data.created > 1 ? `${data.created} appointments scheduled` : "Appointment scheduled");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      if (e.response?.status === 409) setConflict(e.response.data.detail);
      else setError(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="appointment-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Edit / reschedule job" : "Schedule a job"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {!editing && <SelectField label="Client *" value={form.client_id} onChange={(v) => set("client_id", v)} options={clients.map((c) => [c.id, c.name])} testId="appt-client-select" />}
            <SelectField label="Service type" value={form.service_type} onChange={(v) => set("service_type", v)} options={Object.entries(SERVICE_LABELS)} testId="appt-service-select" />
            <Field label={`Start (${tz})`} htmlFor="appt-start"><Input id="appt-start" type="datetime-local" required value={form.start_local} onChange={(e) => set("start_local", e.target.value)} className="h-11" data-testid="appt-start-input" /></Field>
            <Field label="Duration (minutes)" htmlFor="appt-dur"><Input id="appt-dur" type="number" min={15} step={15} required value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="h-11" data-testid="appt-duration-input" /></Field>
            <Field label="Price ($)" htmlFor="appt-price"><Input id="appt-price" type="number" min={0} step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="h-11" data-testid="appt-price-input" /></Field>
            <SelectField label="Assigned cleaner" value={form.assigned_user_id} onChange={(v) => set("assigned_user_id", v)} options={[[NONE, "Unassigned"], ...team.map((t) => [t.id, t.name])]} testId="appt-assignee-select" />
            {!editing && <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={["requested", "confirmed"].map((s) => [s, STATUS_LABELS[s]])} testId="appt-status-select" />}
            {!editing && <SelectField label="Checklist" value={form.checklist_template_id} onChange={(v) => set("checklist_template_id", v)} options={[[NONE, "No checklist"], ...templates.map((t) => [t.id, t.client_name ? `${t.name} (${t.client_name})` : t.name])]} testId="appt-checklist-select" />}
          </div>
          {!editing && (
            <div className="rounded-lg border border-slate-200 p-4 grid sm:grid-cols-3 gap-4" data-testid="recurrence-section">
              <SelectField label="Repeats" value={form.frequency} onChange={(v) => set("frequency", v)} options={[["none", "Does not repeat"], ["weekly", "Weekly"], ["biweekly", "Every 2 weeks"], ["monthly", "Monthly"], ["custom", "Custom interval"]]} testId="appt-frequency-select" />
              {form.frequency === "custom" && <Field label="Every (days)" htmlFor="appt-int"><Input id="appt-int" type="number" min={1} value={form.interval_days} onChange={(e) => set("interval_days", e.target.value)} className="h-11" data-testid="appt-interval-input" /></Field>}
              {form.frequency !== "none" && <Field label="Number of visits" htmlFor="appt-occ"><Input id="appt-occ" type="number" min={1} max={104} value={form.occurrences} onChange={(e) => set("occurrences", e.target.value)} className="h-11" data-testid="appt-occurrences-input" /></Field>}
              {form.frequency !== "none" && <Field label="End date (optional)" htmlFor="appt-end"><Input id="appt-end" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="h-11" data-testid="appt-end-date-input" /></Field>}
            </div>
          )}
          {editing && appointment.series_id && (
            <Field label="Apply changes to">
              <RadioGroup value={form.scope} onValueChange={(v) => set("scope", v)} className="flex gap-6" data-testid="appt-scope-radio">
                <label className="flex items-center gap-2 min-h-[44px]"><RadioGroupItem value="this" data-testid="scope-this" />This visit only</label>
                <label className="flex items-center gap-2 min-h-[44px]"><RadioGroupItem value="future" data-testid="scope-future" />This and future visits</label>
              </RadioGroup>
            </Field>
          )}
          <Field label="Job notes" htmlFor="appt-notes"><Textarea id="appt-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="appt-notes-input" /></Field>
          {conflict && <ConflictBox conflict={conflict} tz={tz} />}
          {error && <p className="text-sm text-red-700" role="alert" data-testid="appt-form-error">{error}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="appt-cancel-btn">Close</Button>
            {conflict
              ? <Button type="button" disabled={busy} onClick={() => submit(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="appt-force-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Schedule anyway</Button>
              : <Button type="submit" disabled={busy} data-testid="appt-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{editing ? "Save changes" : "Schedule job"}</Button>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
